"use client";

import { useEffect, useRef, useState } from "react";

import { getCurrentPrices } from "./api-client";

// Live prices for a page already on screen (plan 2.4). Polls
// /api/prices/current until the socket `prices` channel is wired; the
// server-rendered HTML already carries real prices, so this only patches
// the figures that move and marks them for a brief highlight.
const POLL_MS = 30_000;
const HIGHLIGHT_MS = 2_000;

export interface LivePrices {
  /** Latest known price per product id (Rial string, or null when halted). */
  prices: Record<string, string | null>;
  updatedAt: string | null;
  /** Product ids whose price changed in the last poll. */
  changed: Set<string>;
}

export function useLivePrices(initial: { updatedAt: string | null }): LivePrices {
  const [state, setState] = useState<LivePrices>({ prices: {}, updatedAt: initial.updatedAt, changed: new Set() });
  const last = useRef<Record<string, string | null>>({});

  useEffect(() => {
    let cancelled = false;
    let clearTimer: ReturnType<typeof setTimeout> | undefined;

    async function poll() {
      const { status, data } = await getCurrentPrices().catch(() => ({ status: 0, data: null }));
      if (cancelled || status !== 200 || !data) return;
      const changed = new Set(
        Object.keys(data.prices).filter((id) => id in last.current && last.current[id] !== data.prices[id]),
      );
      last.current = data.prices;
      setState({ prices: data.prices, updatedAt: data.updatedAt, changed });
      if (changed.size) {
        clearTimeout(clearTimer);
        clearTimer = setTimeout(() => setState((s) => ({ ...s, changed: new Set() })), HIGHLIGHT_MS);
      }
    }

    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearTimeout(clearTimer);
    };
  }, []);

  return state;
}

/**
 * «۱۲ ثانیه پیش», re-rendered every few seconds. Null until mounted: the
 * server's clock and the browser's differ, so rendering it in the server
 * HTML would fail hydration.
 */
export function useSecondsAgo(iso: string | null): string | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(timer);
  }, []);
  if (!iso || now === null) return null;
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds.toLocaleString("fa-IR")} ثانیه پیش`;
  return `${Math.round(seconds / 60).toLocaleString("fa-IR")} دقیقه پیش`;
}
